"use client"

import Paragraph from "@/components/common/Typography/Paragraph"
import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { tourActions } from "@/lib/redux/features/tour/reduce"
import { TypeTour } from "@/lib/redux/features/tour/types"
import { useAppDispatch } from "@/lib/redux/hooks"


import { getQueryStringTourDetail } from "@/utils/string/getQueryStringTourDetail"
import { getTourRatingColor } from "@/utils/tour/getTourRatingColor"
import { getTourRatingStatus, resolveTourRating } from "@/utils/tour/getTourRatingStatus"
import { formatTourHighlights, parseTourName } from "@/utils/tour/parseTourName"
import { faCalendarAlt, faClock } from "@fortawesome/free-regular-svg-icons"
import { faPlane, faPlaneArrival, faPlaneDeparture } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Badge, Card, Col, Row, Tag, theme, Tooltip } from "antd"
import { useTranslation } from "react-i18next"

type TourCardProps = {
  size?: 'default' | 'small',
  tour: TypeTour
}

const TourCard = ({ size = 'default', tour }: TourCardProps) => {
  const token = theme.useToken()
  const dispatch = useAppDispatch()
  const { getFormatCurrency, getValidDayjs } = useTETranslation()
  const { t, i18n } = useTranslation()

  const highlights = formatTourHighlights(tour?.hightLights || "")
  const highlightsClamped = highlights.split("\n").length > 4

  const splitedName = parseTourName(tour.tourName).split('|').reduce((arr: any[], str) => {
    if (str === tour.tourName) {
      return [str]
    }
    return arr.concat([str])
  }, [])

  const airline = splitedName.length < 2 ? '' : splitedName[splitedName.length - 1].trim()
  const tourUrl = `/${i18n.language}/tours/detail/${tour.tourId}?${getQueryStringTourDetail(tour, tour.categoryId)}`

  const onCardClick = () => {
    dispatch(tourActions.addCurrentlyChoosen(tour))
  }

  const renderPrice = (small?: boolean) => {
    return (
      <Title style={{ margin: 0, whiteSpace: 'nowrap' }} type='warning' level={small ? 4 : 3}>
        {getFormatCurrency(parseFloat(tour.priceDefault))}
      </Title>
    )
  }

  const renderStartDate = () => {
    return (
      <Text style={{ color: token.token.colorPrimaryText }}><FontAwesomeIcon icon={faCalendarAlt} /> {getValidDayjs(tour?.startDate).format("DD-MM-YYYY")}</Text>
    )
  }

  const renderName = (small?: boolean) => {
    if (small) {
      return (
        <Row style={{ marginBottom: '1rem' }}>
          <Col>
            <Paragraph ellipsis={{ rows: 3 }} style={{ margin: 0 }} className="te-text-small">
              {parseTourName(tour?.tourName)}
            </Paragraph>
          </Col>
        </Row>
      )
    }
    return (
      <Row style={{ height: '9rem' }}>
        <Col>
          <Title ellipsis={{ rows: 3 }} level={5} style={{ margin: 0 }}>
            {splitedName[0].trim()}
          </Title>
        </Col>
      </Row>
    )
  }

  const renderFeedback = () => {
    return (
      <Row style={{ marginBlock: '0.5rem' }} justify='space-between' align='middle'>
        <Col>
          <Row>
            <Text style={{ color: token.token.colorPrimaryText }}>
              <FontAwesomeIcon icon={faClock} /> {tour?.totalDay} {t("common.day")} {tour?.totalNight} {t("common.night")}
            </Text>
          </Row>
        </Col>
        <Col>
          {(!!tour?.ticketPlaneBack || !!tour?.ticketPlaneGo) && (
            <Tooltip
              title={t("tour.travel_by_plane")}
            >
              <FontAwesomeIcon icon={faPlaneDeparture} />
            </Tooltip>
          )}
        </Col>
      </Row>
    )
    const displayRating = resolveTourRating(tour.id)
    return (
      <Row align='middle' style={{ marginTop: '1rem' }}>
        <Col>
          <Tag style={{ fontWeight: 'bold' }} color={getTourRatingColor(displayRating.score)}>
            {displayRating.score.toFixed(1)}
          </Tag>
        </Col>
        <Col>
          <Text strong type={displayRating.score > 8 ? 'success' : displayRating.score > 5 ? 'warning' : 'danger'}>
            {getTourRatingStatus(displayRating.score)}
          </Text>
        </Col>
        {/* <Divider orientation='vertical' />
        <Text type='secondary'>
          2 đánh giá
        </Text> */}
      </Row>
    )
  }

  let content = (
    <Card
      variant='outlined'
      hoverable
      size='small'
      className="tours--item__card"
      style={{ height: '100%' }}
      styles={{
        body: {
          height: "100%"
        }
      }}
      cover={
        <img
          style={{ height: "22rem", objectFit: 'cover' }}
          draggable={false}
          alt={tour?.thumbnail?.name || "default tour image"}
          src={tour?.thumbnail?.path || "/static/images/tour/default_image.png"}
        />
      }
    >
      {renderName()}
      <Row align={'bottom'} style={{ height: `calc(100% - ${size == 'small' ? '30rem' : '32rem'})` }}>
        <Col span={24}>
          {highlights && (
            <div className="tours--item__card--highlights">
              {highlightsClamped ? (
                <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{highlights}</span>}>
                  <span className="tours--item__card--highlights-text tours--item__card--highlights-text--clamp">
                    {highlights}
                  </span>
                </Tooltip>
              ) : (
                <span className="tours--item__card--highlights-text">{highlights}</span>
              )}
            </div>
          )}
          {renderStartDate()}
          {renderFeedback()}
          {/* <Row justify='end' style={{ marginTop: '2rem' }}>
        <Title style={{ margin: 0, textDecoration: 'line-through' }} type='secondary' delete level={4}>
          {getFormatCurrency(33899000)}
        </Title>
      </Row> */}
          <Row wrap={false} justify='space-between'>
            <Col>
              <Text style={{ color: token.token.colorPrimaryText }}>{airline}</Text>
            </Col>
            <Col>
              {renderPrice(true)}
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  )
  if (size === 'small') {
    content = (
      <Card
        style={{
          height: '100%'
        }}
        variant='outlined'
        styles={{
          body: {
            padding: 0,
            height: "100%"
          }
        }}
        hoverable
      >
        <Row wrap={false} style={{ height: "100%" }}>
          <Col span={8}>
            <img
              width={'100%'}
              height={"100%"}
              style={{ borderTopLeftRadius: 8, borderBottomLeftRadius: 8, height: '100%', objectFit: 'cover' }}
              draggable={false}
              alt={tour?.thumbnail?.name || "default tour image"}
              src={tour?.thumbnail?.path || "/static/images/tour/default_image.png"}
            />
          </Col>
          <Col
            flex={1}
            style={{
              padding: '1.2rem',
              height: '100%'
            }}
          >
            <Row align='stretch' style={{ width: '100%', height: '100%' }}>
              <Col
              >
                {renderName(true)}
              </Col>
              <Col span={24} style={{ height: 'fit-content', alignSelf: 'end' }}>
                {renderStartDate()}
                {renderFeedback()}
                <Row justify='space-between'>
                  {renderPrice(true)}
                </Row>
              </Col>

            </Row>

          </Col>
        </Row>
      </Card>
    )
  }
  if (tour?.badgeName) {
    content = (
      <Badge.Ribbon
        placement='start'
        text={tour?.badgeName}
        color="red"
        rootClassName="tours--item__card--wrapper"
      >
        {content}
      </Badge.Ribbon>
    )
  }

  return (
    <a
      href={tourUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="tours--item__card-link"
      onClick={onCardClick}
    >
      {content}
    </a>
  )
}

TourCard.displayName = 'TourCard'

export default TourCard